/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';

import MailServerModel from 'cio/models/mail-server';
import AlertsService from 'cio/services/alerts';
import ApiErrorsService from 'cio/services/api-errors';
import EnvironmentService from 'cio/services/environment';

import { updateConfigurableDenominators } from 'cio/utils/metrics/measure';
import MetricsService from 'cio/services/metrics';
import AnalyticsService from 'cio/services/analytics';

import { storageFor } from 'ember-local-storage';

interface CustomSmtpSettingsArgs {
  servers: MailServerModel[];
}

export default class CustomSmtpSettings extends Component<CustomSmtpSettingsArgs> {
  @storageFor('cio-alerts') localStorage: any;

  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service metrics!: MetricsService;
  @service environment!: EnvironmentService;

  @tracked showCreateServerModal = false;
  @tracked serverOpenForEditing?: MailServerModel;

  get existingServers() {
    return this.args.servers.filter((server) => !server.isNew);
  }

  saveMetricConfigurationTask = task({ drop: true }, async () => {
    try {
      await this.environment.current.save();
      updateConfigurableDenominators(this.environment.current.metricDenominator);
      this.metrics.clearAllMetrics();
      this.alerts.success('metric_config.success', 'Metric configuration updated');
    } catch (e) {
      this.environment.current.rollbackAttributes();
      this.apiErrors.report(e);
    }
  });

  saveServerTask = task({ drop: true }, async (server: MailServerModel) => {
    let response = await server.save();
    this.serverOpenForEditing = undefined;

    return response;
  });

  deleteServerTask = task({ drop: true }, async (server: MailServerModel) => {
    try {
      await server.destroyRecord();
      this.alerts.success('custom_smtp.delete.success', 'Custom SMTP server deleted');
    } catch {
      this.alerts.success('custom_smtp.delete.error', 'Unable to delete custom SMTP server');
    }
  });

  @action
  rollbackMetricConfiguration() {
    this.environment.current.rollbackAttributes();
  }

  @action
  onAddNewCustomSmtp() {
    this.showCreateServerModal = true;
    this.analytics.track('email', 'custom_smtp_add_server_started');
  }
}
